import { Component, inject, OnInit, signal } from "@angular/core";
import { FaIconLibrary, FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faFileExport, faList, faTable } from "@fortawesome/free-solid-svg-icons";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { map, Observable } from "rxjs";

import { DubaiTimePipe } from "../../core/pipes";
import { AdaaHelper } from "../../core/utils";
import { NestedTableComponent, ObjectsTreeComponent, PeriodSliderVariantComponent } from "../../shared/components";
import { genericFloatButtons } from "../../shared/components/general/float-action";
import { Constants } from "../../shared/constants/constants";
import { Language } from "../../shared/constants/enums";
import {
  navigationPlan,
  ObjectiveModelType,
  SelectedPeriodModelType,
  StructureViewColumnConfig,
} from "../../shared/models";
import {
  ObjectivesApiService,
  PrintService,
  PropertiesService,
  StructureViewService,
  SystemLayoutService,
} from "../../shared/services";

@Component({
  selector: "adaa-plan-navigation",
  imports: [
    FontAwesomeModule,
    PeriodSliderVariantComponent,
    NestedTableComponent,
    ObjectsTreeComponent,
    DubaiTimePipe,
    TranslatePipe,
  ],
  templateUrl: "./plan-navigation.html",
  styleUrl: "./plan-navigation.scss",
})
export class PlanNavigationComponent implements OnInit {
  private _objectivesApiService = inject(ObjectivesApiService);
  private _propertiesService = inject(PropertiesService);
  private _systemLayoutService = inject(SystemLayoutService);
  private _iconLibrary = inject(FaIconLibrary);
  structureViewService = inject(StructureViewService);
  private _instantTranslate = inject(TranslateService);
  private _printService = inject(PrintService);

  private readonly _genericFloatButtons = genericFloatButtons();
  private readonly _untilDestroyed = AdaaHelper.untilDestroyed();
  constants = Constants;
  planPeriod = signal<{ startDate: number; endDate: number }>({ startDate: 0, endDate: 0 });
  objectives = signal<ObjectiveModelType[]>([]);
  columns: StructureViewColumnConfig[] = [
    {
      title: "common.tables.type",
      width: "15%",
      type: "type",
      alignment: AdaaHelper.getCurrentLang() === Language.Arabic ? "R" : "L",
    },
    {
      title: "common.tables.name",
      width: "25%",
      type: "text",
      valueKey: AdaaHelper.getFieldLanguage("name"),
      alignment: AdaaHelper.getCurrentLang() === Language.Arabic ? "R" : "L",
      showTags: AdaaHelper.isPlanArchived(),
    },
    {
      title: "common.tables.performance",
      width: "15%",
      type: "percentage",
      colorKey: "evaluationPerformance",
      valueKey: "performance",
    },
    {
      title: "ekpi.ytp",
      width: "15%",
      type: "percentage",
      colorKey: "normalizedAnnualPerformanceColor",
      valueKey: "normalizedAnnualPerformance",
    },
    {
      title: "common.tables.info",
      width: "10%",
      type: "status",
      valueKey: "info",
    },
    {
      title: "common.tables.trend",
      width: "10%",
      type: "trend",
      valueKey: "trend",
    },
    {
      title: "common.tables.actions",
      width: "8%",
      type: "actions",
      valueKey: "type",
    },
  ];
  columnsTree: StructureViewColumnConfig[] = AdaaHelper.isPlanArchived()
    ? [
        {
          title: "common.tables.name",
          width: "35%",
          type: "text",
          valueKey: AdaaHelper.getFieldLanguage("name"),
          alignment: AdaaHelper.getCurrentLang() === Language.Arabic ? "R" : "L",
          showTags: true,
        },
        {
          title: "common.tables.period_performance_short",
          width: "15%",
          type: "percentage",
          colorKey: "evaluationPerformance",
          valueKey: "performance",
        },
        {
          title: "common.tables.annual_performance_short",
          width: "15%",
          type: "percentage",
          colorKey: "evaluationYtp",
          valueKey: "ytp",
        },
        {
          title: "common.tables.info",
          width: "15%",
          type: "status",
          valueKey: "info",
        },
        {
          title: "common.tables.trend",
          width: "10%",
          type: "trend",
          valueKey: "trend",
        },
        {
          title: "common.tables.actions",
          width: "8%",
          type: "actions",
          valueKey: "type",
        },
      ]
    : [
        {
          title: "common.tables.type",
          width: "15%",
          type: "type",
          alignment: AdaaHelper.getCurrentLang() === Language.Arabic ? "R" : "L",
        },
        {
          title: "common.tables.name",
          width: "25%",
          type: "text",
          valueKey: AdaaHelper.getFieldLanguage("name"),
          alignment: AdaaHelper.getCurrentLang() === Language.Arabic ? "R" : "L",
        },
        {
          title: "common.tables.period_performance_short",
          width: "15%",
          type: "percentage",
          colorKey: "evaluationPerformance",
          valueKey: "performance",
        },
        {
          title: "common.tables.annual_performance_short",
          width: "15%",
          type: "percentage",
          colorKey: "evaluationYtp",
          valueKey: "ytp",
        },
        {
          title: "common.tables.info",
          width: "10%",
          type: "status",
          valueKey: "info",
        },
        {
          title: "common.tables.trend",
          width: "10%",
          type: "trend",
          valueKey: "trend",
        },
        {
          title: "common.tables.actions",
          width: "8%",
          type: "actions",
          valueKey: "type",
        },
      ];

  public ngOnInit(): void {
    this._setPeriod();
    this._getTypes();
    this._floatActions();

    this._iconLibrary.addIcons(faTable, faList, faFileExport);

    this._systemLayoutService.hasCycleChanged$.pipe(this._untilDestroyed()).subscribe({
      next: () => window.location.reload(),
    });
    this._systemLayoutService.hasActiveEntityChanged$.pipe(this._untilDestroyed()).subscribe({
      next: () => window.location.reload(),
    });
  }

  public selectPeriod(period: Pick<SelectedPeriodModelType, "period">) {
    if (!period) return;
    this.structureViewService.selectedPeriod.update(() => period);
    this._getObjectives();
  }

  private _getObjectives() {
    if (!AdaaHelper.isDefined(this.structureViewService.selectedPeriod())) return;

    this._objectivesApiService
      .getItemObjectivesByEntityAndPlanV2({
        entityId: AdaaHelper.entity?.id as number,
        planId: AdaaHelper.plan?.id as number,
        periodId: this.structureViewService.selectedPeriod()!.period.id,
      })
      .subscribe({
        next: (response) => {
          this.objectives.set(response.responseData);
        },
      });
  }

  private _setPeriod(): void {
    const plan = AdaaHelper.plan as { startYear: number; endYear: number };
    if (!plan) return;
    const dates = {
      startDate: plan.startYear,
      endDate: plan.endYear,
    };
    this.planPeriod.set(dates);
  }

  private _getTypes(): void {
    this._propertiesService.getAllItemTypes().subscribe({
      next: (response) => {
        if (response.inError) return;

        this.structureViewService.types.set(response.responseData);
      },
    });
  }

  private _floatActions(): void {
    this._genericFloatButtons([
      {
        key: "helpdesk",
        data: {
          url: "/helpdesk/create",
        },
      },
      {
        key: "print",
        data: undefined,
      },
    ]);
  }

  public printContent(): void {
    const translate = (key: string) => this._instantTranslate.instant(key);

    const columns = [
      { key: "objectTypeName", label: translate("common.tables.type") },
      { key: "nameEN", label: translate("common.tables.name") },
      { key: "performanceFormatted", label: translate("common.tables.period_performance_short") },
      { key: "annualFormatted", label: translate("common.tables.annual_performance_short") },
      { key: "infoName", label: translate("common.tables.info") },
      { key: "trendName", label: translate("common.tables.trend") },
    ];

    const lang = AdaaHelper.getCurrentLang() === Language.Arabic ? "AE" : "EN";
    const { id: entityId } = AdaaHelper.entity || {};
    const { id: planId } = AdaaHelper.plan || {};
    const period = this.structureViewService.selectedPeriod()?.period;
    const logo = this._systemLayoutService.activeEntityLogo();
    const titleText = `${this._instantTranslate.instant("navigation.plan_navigation")} ${period?.month}/${period?.year}`;
    const customStyles = `th { background-color: #f0f0f0; }`;

    if (!entityId || !planId || !period?.id) return;

    this._objectivesApiService
      .getItemPlanNavigationByEntityAndPlan({ entityId, planId, periodId: period.id })
      .subscribe({
        next: (response) => {
          this._populateNavigationPlans(response.responseData, lang).subscribe({
            next: (data) => {
              this._printService.printTable({
                data,
                columns,
                title: titleText,
                rtl: lang === "AE",
                indentKey: "children",
                logoUrl: logo,
                rowHeaderIndex: 0,
                customStyles,
              });
            },
          });
        },
      });
  }

  private _populateNavigationPlans(items: navigationPlan[], lang: "AE" | "EN"): Observable<navigationPlan[]> {
    const propertyIds = [
      this.constants.CONSTANT_TREND,
      this.constants.CONSTANT_STATUS_TYPE,
      this.constants.CONSTANT_OBJECTIVE_TYPE_PROP_ID,
      this.constants.CONSTANT_ACTIVITY_TYPE_PROP_ID,
      this.constants.CONSTANT_KPITYPE_PROP,
      this.constants.CONSTANT_SERVICETYPES_TYPE,
      this.constants.CONSTANT_INITIATIVES_TYPE_PROP_ID,
      this.constants.CONSTANT_ITEMTYPE,
    ];

    return this._propertiesService.getPropByIdList(propertyIds).pipe(
      map((response) => {
        const properties = response?.responseData ?? [];

        const getPropertyName = (value: string | number | null | undefined, matchById: boolean = true): string => {
          if (value == null) return "-";
          const match = matchById
            ? properties.find((prop) => prop.id === value)
            : properties.find((prop) => prop.value === String(value));
          return match ? (lang === "AE" ? match.nameAE : match.nameEN) : "-";
        };

        const populatedItem = (
          item: navigationPlan
        ): navigationPlan & {
          objectTypeName?: string;
          trendName?: string;
          infoName?: string;
          performanceFormatted?: string;
          annualFormatted?: string;
          nameLocalized?: string;
          children?: navigationPlan[];
        } => {
          const populated = {
            ...item,
            objectTypeName: getPropertyName(item.objectSubType ?? item.objectType),
            trendName: getPropertyName(item.trend),
            infoName: getPropertyName(item.info, false),
            performanceFormatted: item.performance != null ? `${Number(item.performance).toFixed(0)}%` : "-",
            annualFormatted:
              item.normalizedAnnualPerformance != null
                ? `${Number(item.normalizedAnnualPerformance).toFixed(0)}%`
                : "-",
            nameLocalized: lang === "AE" ? item.nameAE : item.nameEN,
            children: Array.isArray(item.children) ? item.children.map((child) => populatedItem(child)) : undefined,
          };
          return populated;
        };

        return items.map(populatedItem);
      })
    );
  }
}
